import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = useAuth();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Data states for each tab
  const [tasks, setTasks] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [customPageSubmissions, setCustomPageSubmissions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [pendingData, setPendingData] = useState([]);
  const [completedData, setCompletedData] = useState([]);

  // Loading states for each tab
  const [tasksLoading, setTasksLoading] = useState(false);
  const [formsLoading, setFormsLoading] = useState(false);
  const [customPagesLoading, setCustomPagesLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);

  const canViewTasks = checkPermission('View Tasks');
  const canViewForms = checkPermission('View Forms and Tasks');
  const canViewCustomPages = checkPermission('View Custom Page Submissions');

  useEffect(() => {
    fetchClientDetails();
  }, [clientId]);

  useEffect(() => {
    if (client) {
      fetchTabData();
    }
  }, [client, activeTab, searchTerm, statusFilter]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/master-data/${clientId}`);
      setClient(response.data.data);
    } catch (error) {
      console.error('Error fetching client details:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async () => {
    switch (activeTab) {
      case 'tasks':
        await fetchTasks();
        break;
      case 'forms':
        await fetchFormSubmissions();
        break;
      case 'custom-pages':
        await fetchCustomPageSubmissions();
        break;
      case 'logs':
        await fetchLogs();
        break;
      case 'pending':
        await fetchPendingData();
        break;
      case 'completed':
        await fetchCompletedData();
        break;
    }
  };

  const fetchTasks = async () => {
    try {
      setTasksLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter
      });
      const response = await api.get(`/client/${clientId}/tasks?${params}`);
      setTasks(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchFormSubmissions = async () => {
    try {
      setFormsLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter
      });
      const response = await api.get(`/client/${clientId}/form-submissions?${params}`);
      setFormSubmissions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching form submissions:', error);
      toast.error('Failed to load form submissions');
    } finally {
      setFormsLoading(false);
    }
  };

  const fetchCustomPageSubmissions = async () => {
    try {
      setCustomPagesLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        status: statusFilter
      });
      const response = await api.get(`/client/${clientId}/custom-page-submissions?${params}`);
      setCustomPageSubmissions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching custom page submissions:', error);
      toast.error('Failed to load custom page submissions');
    } finally {
      setCustomPagesLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams({
        search: searchTerm
      });
      const response = await api.get(`/client/${clientId}/logs?${params}`);
      setLogs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchPendingData = async () => {
    try {
      setPendingLoading(true);
      const params = new URLSearchParams({
        search: searchTerm
      });
      
      const response = await api.get(`/client/${clientId}/pending?${params}`);
      setPendingData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching pending data:', error);
      toast.error('Failed to load pending data');
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchCompletedData = async () => {
    try {
      setCompletedLoading(true);
      const params = new URLSearchParams({
        search: searchTerm
      });
      
      const response = await api.get(`/client/${clientId}/completed?${params}`);
      setCompletedData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching completed data:', error);
      toast.error('Failed to load completed data');
    } finally {
      setCompletedLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'task':
        return <ClipboardDocumentListIcon className="h-5 w-5 text-blue-500" />;
      case 'form':
        return <DocumentTextIcon className="h-5 w-5 text-green-500" />;
      case 'custom-page':
        return <UserGroupIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <ChartBarIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTasks = () => (
    <div className="space-y-4">
      {tasksLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No tasks found for this client</div>
      ) : (
        <div className="grid gap-4">
          {tasks.map((task) => (
            <div key={task._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(task.status)}
                  <div>
                    <h3 className="font-medium text-gray-900">{task.name}</h3>
                    <p className="text-sm text-gray-500">{task.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(task.createdAt)}</div>
                  <div className="text-xs text-gray-400">Deadline: {formatDate(task.deadline)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderFormSubmissions = () => (
    <div className="space-y-4">
      {formsLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : formSubmissions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No form submissions found for this client</div>
      ) : (
        <div className="grid gap-4">
          {formSubmissions.map((submission) => (
            <div key={submission._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(submission.status)}
                  <div>
                    <h3 className="font-medium text-gray-900">{submission.formName || 'Form Submission'}</h3>
                    <p className="text-sm text-gray-500">
                      Submitted by: {submission.userEmail}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(submission.createdAt)}</div>
                  <div className="text-xs text-gray-400">
                    Total Fees: ${submission.totalFees?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCustomPageSubmissions = () => (
    <div className="space-y-4">
      {customPagesLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : customPageSubmissions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No custom page submissions found for this client</div>
      ) : (
        <div className="grid gap-4">
          {customPageSubmissions.map((submission) => (
            <div key={submission._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(submission.status)}
                  <div>
                    <h3 className="font-medium text-gray-900">{submission.name}</h3>
                    <p className="text-sm text-gray-500">
                      Page: {submission.customPage?.name || 'Unknown Page'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(submission.createdAt)}</div>
                  <div className="text-xs text-gray-400">
                    Submitted by: {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderLogs = () => (
    <div className="space-y-4">
      {logsLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No logs found for this client</div>
      ) : (
        <div className="grid gap-4">
          {logs.map((log) => (
            <div key={log._id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div>
                    <h3 className="font-medium text-gray-900">{log.actionType}</h3>
                    <p className="text-sm text-gray-500">{log.details}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(log.metadata?.timestamp)}</div>
                  <div className="text-xs text-gray-400">
                    By: {log.userId?.firstName} {log.userId?.lastName}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPendingData = () => (
    <div className="space-y-4">
      {pendingLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : pendingData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No pending items found for this client</div>
      ) : (
        <div className="grid gap-4">
          {pendingData.map((item) => (
            <div key={`${item.type}-${item._id}`} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getTypeIcon(item.type)}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {item.name || item.formName || 'Unknown Item'}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">{item.type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(item.createdAt)}</div>
                  <div className="text-xs text-gray-400">Status: {item.status}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCompletedData = () => (
    <div className="space-y-4">
      {completedLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : completedData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No completed items found for this client</div>
      ) : (
        <div className="grid gap-4">
          {completedData.map((item) => (
            <div key={`${item.type}-${item._id}`} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getTypeIcon(item.type)}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {item.name || item.formName || 'Unknown Item'}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">{item.type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{formatDate(item.updatedAt || item.createdAt)}</div>
                  <div className="text-xs text-gray-400">Completed: {formatDate(item.completedAt || item.updatedAt)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentListIcon, count: tasks.length },
    { id: 'forms', name: 'Forms', icon: DocumentTextIcon, count: formSubmissions.length },
    { id: 'custom-pages', name: 'Custom Pages', icon: UserGroupIcon, count: customPageSubmissions.length },
    { id: 'logs', name: 'Logs', icon: ChartBarIcon, count: logs.length },
    { id: 'pending', name: 'Pending', icon: ClockIcon, count: pendingData.length },
    { id: 'completed', name: 'Completed', icon: CheckCircleIcon, count: completedData.length }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Client not found</h2>
          <button
            onClick={() => navigate('/master-data')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Master Data
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/master-data')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Master Data
          </button>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{client.clientName}</h1>
            <p className="text-gray-600 mb-4">{client.clientAddress}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-gray-600">{client.clientEmail || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Phone:</span>
                <span className="ml-2 text-gray-600">{client.clientPhone || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Location:</span>
                <span className="ml-2 text-gray-600">
                  {[client.clientCity, client.clientState, client.clientCountry].filter(Boolean).join(', ') || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.name}</span>
                    {tab.count > 0 && (
                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'tasks' && renderTasks()}
            {activeTab === 'forms' && renderFormSubmissions()}
            {activeTab === 'custom-pages' && renderCustomPageSubmissions()}
            {activeTab === 'logs' && renderLogs()}
            {activeTab === 'pending' && renderPendingData()}
            {activeTab === 'completed' && renderCompletedData()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetails; 